/*
 * m2-calculator
 *
 * Lets the customer enter the area they need in m² while the store keeps
 * selling whole boxes. The real Shopify quantity input is kept in the form and
 * driven by this element, so add to cart, cart and checkout all keep working
 * with box quantities exactly as Shopify expects.
 *
 * Kept in its own asset file on purpose. Editing theme.js directly means the
 * next Foxecom theme update overwrites the work.
 */
class M2Calculator extends HTMLElement {
  connectedCallback() {
    this.coverage = parseFloat(this.dataset.coverage);
    this.boxPrice = parseInt(this.dataset.boxPrice, 10);
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}} kr';
    this.wastePercent = parseInt(this.dataset.wastePercent, 10) || 0;

    this.input = this.querySelector('[data-m2-input]');
    this.wasteToggle = this.querySelector('[data-m2-waste]');
    this.boxesOutput = this.querySelector('[data-m2-boxes]');
    this.boxesLabel = this.querySelector('[data-m2-boxes-label]');
    this.areaOutput = this.querySelector('[data-m2-area]');
    this.totalOutput = this.querySelector('[data-m2-total]');
    this.minusButton = this.querySelector('button[name="minus"]');
    this.plusButton = this.querySelector('button[name="plus"]');

    // Nothing usable to calculate with, leave the page alone.
    if (!this.input || !this.coverage || this.coverage <= 0) return;

    this.quantityInput = this.findQuantityInput();
    this.subscriptions = [];

    /*
     * The area the customer actually asked for, kept separately from what the
     * field displays. After snapping, the field shows the purchasable area
     * (boxes × coverage). If we then read the waste percentage back off that
     * displayed number we would apply waste a second time on every recalculation,
     * so the requested figure is only ever updated by real typing.
     */
    this.requestedArea = this.parseArea(this.input.value);

    /*
     * Whether the customer has typed into the field since we last rewrote it.
     * After a snap the field holds our own formatted output, e.g. "100,80",
     * which does not always read back cleanly: a number input rejects the
     * comma outright and reports an empty value. Reading it back in that state
     * reset the calculation to a single box, which is why typing an area and
     * then ticking the spill box gave the wrong answer while the +/- buttons,
     * which never touch the field, worked fine.
     */
    this.typedSinceSnap = false;

    /*
     * The waste toggle's starting state, so a reset returns the customer to the
     * page as it first rendered rather than to a hardcoded unchecked.
     */
    this.wasteDefault = this.wasteToggle ? this.wasteToggle.checked : false;

    this.input.addEventListener('input', this.onType.bind(this));
    this.input.addEventListener('blur', this.onCommit.bind(this));
    this.input.addEventListener('change', this.onCommit.bind(this));

    if (this.wasteToggle) {
      this.wasteToggle.addEventListener('change', this.onCommit.bind(this));
    }
    if (this.minusButton) {
      this.minusButton.addEventListener('click', this.onStep.bind(this, -1));
    }
    if (this.plusButton) {
      this.plusButton.addEventListener('click', this.onStep.bind(this, 1));
    }

    this.watchAddToCart();

    // The theme resets the quantity input on variant change. Re-apply our
    // value afterwards so the two never disagree.
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      this.subscriptions.push(
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityUpdate, () => {
          this.quantityInput = this.findQuantityInput();
          this.update();
        })
      );
    }

    this.update();
  }

  disconnectedCallback() {
    this.subscriptions.forEach((unsubscribe) => {
      if (typeof unsubscribe === 'function') unsubscribe();
    });
    this.subscriptions = [];
    if (this.resetTimer) clearTimeout(this.resetTimer);
  }

  /*
   * Looked up through the form rather than by id. buy-buttons.liquid renders
   * id="Quantity-{{ section.id }}" inside a snippet where `section` is nil, so
   * every product page carries the same empty id.
   */
  findQuantityInput() {
    const form = this.closest('form');
    if (!form) return null;
    return form.querySelector('.quantity__input[name="quantity"]');
  }

  /*
   * After a successful add to cart the theme clears its own quantity input back
   * to 1. Without this the calculator keeps showing the previous area and the
   * spill toggle stays ticked, so the screen and the real quantity disagree and
   * a second click can add a different number of boxes than the customer sees.
   *
   * The reset is armed on submit and fired when the cart confirms, so a failed
   * add (sold out, for instance) leaves the customer's figures intact. The
   * timeout is a backstop for themes that publish no event we recognise.
   */
  watchAddToCart() {
    const form = this.closest('form');
    if (!form) return;

    const arm = () => {
      this.resetArmed = true;
      if (this.resetTimer) clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => this.onCartConfirmed(), 2500);
    };

    form.addEventListener('submit', arm);

    /*
     * Belt and braces. If the theme's product-form element intercepts the
     * button click and calls preventDefault there, no submit event is ever
     * dispatched and the listener above never runs. Arming from the click too
     * costs nothing, since arming twice is idempotent.
     */
    const submitButton = form.querySelector('button[name="add"][type="submit"]');
    if (submitButton) submitButton.addEventListener('click', arm);

    const events = window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS;
    if (!events) return;

    // Event naming varies between Foxecom releases, so subscribe to whichever
    // of these the running theme actually defines.
    ['cartUpdate', 'cartUpdated', 'cartChange', 'cartAdded', 'cartError'].forEach((name) => {
      if (!events[name]) return;
      this.subscriptions.push(
        FoxTheme.pubsub.subscribe(events[name], () => {
          if (name === 'cartError') {
            this.resetArmed = false;
            if (this.resetTimer) clearTimeout(this.resetTimer);
            return;
          }
          this.onCartConfirmed();
        })
      );
    });
  }

  onCartConfirmed() {
    if (!this.resetArmed) return;
    this.resetArmed = false;
    if (this.resetTimer) clearTimeout(this.resetTimer);
    this.reset();
  }

  // Back to one box, spill returned to however the page first rendered it.
  reset() {
    if (this.wasteToggle) this.wasteToggle.checked = this.wasteDefault;
    this.requestedArea = this.coverage;
    this.update(true);
  }

  // Accepts both 1,44 and 1.44 since Swedish keyboards produce the comma.
  parseArea(raw) {
    const normalised = String(raw).replace(',', '.').replace(/[^0-9.]/g, '');
    const value = parseFloat(normalised);
    return isNaN(value) ? 0 : value;
  }

  formatArea(value) {
    return (Math.round(value * 100) / 100).toFixed(2).replace('.', ',');
  }

  get appliedWaste() {
    return this.wasteToggle && this.wasteToggle.checked ? this.wastePercent : 0;
  }

  /*
   * Integer maths on hundredths. Dividing floats directly produces results like
   * 2.0000000000000004, which Math.ceil turns into an extra box the customer
   * did not ask for and has to pay for.
   */
  boxesFor(area) {
    if (area <= 0) return 1;
    const areaUnits = Math.round(area * (1 + this.appliedWaste / 100) * 100);
    const coverageUnits = Math.round(this.coverage * 100);
    if (coverageUnits <= 0) return 1;
    return Math.max(1, Math.ceil(areaUnits / coverageUnits));
  }

  // Typing updates the requested area and the readouts, but does not rewrite
  // the field, otherwise the number jumps around mid keystroke.
  onType() {
    this.requestedArea = this.parseArea(this.input.value);
    this.typedSinceSnap = true;
    this.update();
  }

  /*
   * Finishing an edit, or toggling waste, snaps the field to the purchasable
   * area so the customer sees the coverage they will actually receive.
   *
   * The field is only read back when the customer has typed since the last
   * snap. Otherwise it holds our own formatted output and the requested area
   * we already have in state is the truthful figure.
   */
  onCommit() {
    if (this.typedSinceSnap) {
      const typed = this.parseArea(this.input.value);
      if (typed > 0) this.requestedArea = typed;
    }

    if (this.requestedArea <= 0) {
      this.requestedArea = this.coverage;
    }

    this.update(true);
  }

  onStep(direction, event) {
    event.preventDefault();

    const boxes = Math.max(1, this.boxesFor(this.requestedArea) + direction);

    /*
     * Store the requested area net of waste, so that recalculating from it
     * lands back on the same box count rather than drifting up by one each
     * time the customer uses the steppers with waste enabled.
     */
    this.requestedArea = (boxes * this.coverage) / (1 + this.appliedWaste / 100);
    this.update(true);
  }

  update(snap) {
    const boxes = this.boxesFor(this.requestedArea);
    const purchasableArea = boxes * this.coverage;

    if (snap) {
      this.input.value = this.formatArea(purchasableArea);
      this.typedSinceSnap = false;
    }

    if (this.boxesOutput) this.boxesOutput.textContent = boxes;
    if (this.boxesLabel) {
      // "1 förpackning" but "2 förpackningar".
      this.boxesLabel.textContent =
        boxes === 1 ? this.dataset.labelSingular || 'förpackning' : this.dataset.labelPlural || 'förpackningar';
    }
    if (this.areaOutput) this.areaOutput.textContent = this.formatArea(purchasableArea);

    if (this.totalOutput && window.FoxTheme && FoxTheme.Currency) {
      this.totalOutput.textContent = FoxTheme.Currency.formatMoney(boxes * this.boxPrice, this.moneyFormat);
    }

    this.setQuantity(boxes);
  }

  /*
   * Sets the value then fires a bubbling change event, which is exactly what
   * the theme's own plus and minus buttons do in QuantityInput.onButtonClick.
   * Nothing downstream can tell the difference from a real click.
   */
  setQuantity(boxes) {
    if (!this.quantityInput) return;
    if (parseInt(this.quantityInput.value, 10) === boxes) return;

    this.quantityInput.value = boxes;
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('m2-calculator')) {
  customElements.define('m2-calculator', M2Calculator);
}